/**
 * --------------------------------------------
 * KANYOS Paginator.js
 * License ANZAS
 * --------------------------------------------
 */

import $ from 'jquery'
import template from 'art-template'

/**
 * Constants
 * ====================================================
 */

const NAME = 'Paginator'
const DATA_KEY = 'lte.paginator'
const EVENT_KEY = `.${DATA_KEY}`
const JQUERY_NO_CONFLICT = $.fn[NAME]

const EVENT_PAGE_CHANGED = `pagechanged${EVENT_KEY}`

// const SELECTOR_DATA_TOGGLE = '[data-widget="paginator"]'
const SELECTOR_DATA_PAGINATOR_PAGE = '[paginator-role="page"]'
const SELECTOR_DATA_PAGINATOR_FIRST = '[paginator-role="first"]'
const SELECTOR_DATA_PAGINATOR_LAST = '[paginator-role="last"]'
const SELECTOR_DATA_PAGINATOR_PREVIOUS_JUMP = '[paginator-role="previousJump"]'
const SELECTOR_DATA_PAGINATOR_NEXT_JUMP = '[paginator-role="nextJump"]'
const SELECTOR_DATA_PAGINATOR_PREVIOUS = '[paginator-role="previous"]'
const SELECTOR_DATA_PAGINATOR_NEXT = '[paginator-role="next"]'
const SELECTOR_DATA_PAGINATOR_PAGE_BUTTONS = `${SELECTOR_DATA_PAGINATOR_PAGE},${SELECTOR_DATA_PAGINATOR_FIRST},${SELECTOR_DATA_PAGINATOR_LAST},${SELECTOR_DATA_PAGINATOR_PREVIOUS_JUMP},${SELECTOR_DATA_PAGINATOR_NEXT_JUMP},${SELECTOR_DATA_PAGINATOR_PREVIOUS},${SELECTOR_DATA_PAGINATOR_NEXT}`
const SELECTOR_DATA_PAGINATOR_PAGE_SELECT = '[paginator-role="pageSelect"]'
const SELECTOR_DATA_PAGINATOR_PAGE_INPUT = '[paginator-role="page-input"]'
// const SELECTOR_DATA_PAGINATOR_PAGE_GO_BUTTON = '[paginator-role="page-go-button"]'
const SELECTOR_DATA_PAGINATOR_PAGINATION_INFO_FORM = '[paginator-role="pagination-info-form"]'

const Default = {
  wrapper: '',
  first: '',
  previousJump: '',
  previous: '',
  next: '',
  nextJump: '',
  last: '',
  page: '',
  pageSelect: '',
  totalPages: undefined,
  totalCounts: undefined,
  pageSize: undefined,
  currentPage: undefined,
  visiblePages: undefined,
  jumpPages: undefined,
  disableClass: 'disabled',
  activeClass: 'active',
  infoTemplate: undefined,
  loadingWidget: '.loading',
  onPaging(item) {
    return item
  }
}

/**
 * Class Definition
 * ====================================================
 */

class Paginator {
  constructor(element, config) {
    this._config = config
    this._element = element

    this._init()
  }

  switchPage(pageIndex) {
    if (pageIndex < 1) {
      pageIndex = 1
    } else if (pageIndex > this._config.totalPages) {
      pageIndex = this._config.totalPages
    }

    $(this._config.loadingWidget).show()
    this._config.onPaging.call(this._element, pageIndex)

    $(this._element).trigger($.Event(EVENT_PAGE_CHANGED))
  }

  // Private

  _verify() {
    if (!this._isNumber(this._config.totalPages)) {
      throw new TypeError('[paginator] type error: totalPages')
    }

    if (!this._isNumber(this._config.totalCounts)) {
      throw new TypeError('[paginator] type error: totalCounts')
    }

    if (!this._isNumber(this._config.pageSize)) {
      throw new TypeError('[paginator] type error: pageSize')
    }

    if (!this._isNumber(this._config.currentPage)) {
      throw new TypeError('[paginator] type error: currentPage')
    }

    if (!this._isNumber(this._config.visiblePages)) {
      throw new TypeError('[paginator] type error: visiblePages')
    }

    if (!this._isNumber(this._config.jumpPages)) {
      throw new TypeError('[paginator] type error: jumpPages')
    }

    if (!this._config.totalPages && !this._config.totalCounts) {
      throw new TypeError('[paginator] totalCounts or totalPages is required')
    }

    if (!this._config.totalPages && this._config.totalCounts && !this._config.pageSize) {
      throw new TypeError('[paginator] pageSize is required')
    }

    if (this._config.totalCounts && this._config.pageSize) {
      this._config.totalPages = Math.ceil(this._config.totalCounts / this._config.pageSize)
    }

    if (!this._config.jumpPages) {
      this._config.jumpPages = this._config.visiblePages
    }

    if (this._config.currentPage < 1 || this._config.currentPage > this._config.totalPages) {
      throw new TypeError('[paginator] currentPage is incorrect')
    }

    if (this._config.totalPages < 1) {
      throw new TypeError('[paginator] totalPages cannot be less currentPage')
    }
  }

  _setAttributes() {
    this._element.attr({
      'paginator-totalPages': this._config.totalPages,
      'paginator-totalCounts': this._config.totalCounts,
      'paginator-pageSize': this._config.pageSize
    })
  }

  _extendJquery() {
    $.fn.paginatorHTML = function (s) {
      return s ?
        this.before(s).remove() :
        $('<p>')
          .append(this.eq(0).clone())
          .html()
    }
  }

  _render() {
    this._renderHtml()
    this._setStatus()
    this._bindEvents()
  }

  _renderHtml() {
    const html = []

    if (this._isEnable('page')) {
      html.push(this._buildPages())
    }

    if (this._isEnable('seperator')) {
      html.unshift(this._buildFirstPage())
      html.push(this._buildLastPage())
    }

    if (this._isEnable('pageSelect')) {
      html.push(this._buildItem('pageSelect', this._config.currentPage))
    }

    if (this._isEnable('previous')) {
      html.unshift(this._buildItem('previous', this._config.currentPage - 1))
    }

    if (this._isEnable('previousJump')) {
      html.unshift(this._buildItem('previousJump', this._config.currentPage - this._config.jumpPages))
    }

    if (this._isEnable('first')) {
      html.unshift(this._buildItem('first', 1))
    }

    if (this._isEnable('statistics')) {
      html.unshift(this._buildItem('statistics'))
    }

    if (this._isEnable('next')) {
      html.push(this._buildItem('next', this._config.currentPage + 1))
    }

    if (this._isEnable('nextJump')) {
      html.push(this._buildItem('nextJump', this._config.currentPage + this._config.jumpPages))
    }

    if (this._isEnable('last')) {
      html.push(this._buildItem('last', this._config.totalPages))
    }

    if (this._config.wrapper) {
      this._element.html(
        $(this._config.wrapper)
          .html(html.join(''))
          .paginatorHTML()
      )
    } else {
      this._element.html(html.join(''))
    }

    if (this._config.infoTemplate !== undefined) {
      const templateId = this._config.infoTemplate.attr('id')
      this._element.append(
        template(templateId, {
          totalPages: this._config.totalPages,
          inputSelector: 'paginator-role="page-input"'
          // goButtonSelector: 'paginator-role="page-go-button"'
        })
      )
    }
  }

  _buildPages() {
    const html = []

    // const pages = this._getPages()
    const pages = this._getFixedPages()
    for (let i = 0, j = pages.length; i < j; i++) {
      html.push(this._buildItem('page', pages[i]))
    }

    return html.join('')
  }

  _buildFirstPage() {
    const html = []

    const visibleHalf = Math.ceil(this._config.visiblePages / 2)

    if (this._config.totalPages === this._config.visiblePages + 1) {
      if (this._config.currentPage > visibleHalf) {
        html.push(this._buildItem('page', 1))
      }
    } else if (this._config.totalPages > this._config.visiblePages + 1) {
      const marginLeft = this._config.currentPage - 1
      if (marginLeft === visibleHalf) {
        html.push(this._buildItem('page', 1))
      } else if (marginLeft > visibleHalf) {
        html.push(this._buildItem('page', 1), this._buildItem('seperator'))
      }
    }

    return html.join('')
  }

  _buildLastPage() {
    const html = []

    const visibleHalf = Math.ceil(this._config.visiblePages / 2)

    if (this._config.totalPages === this._config.visiblePages + 1) {
      if (this._config.currentPage > visibleHalf) {
        html.push(this._buildItem('page', this._config.totalPages))
      }
    } else if (this._config.totalPages > this._config.visiblePages + 1) {
      const marginRight = this._config.totalPages - this._config.currentPage
      if (marginRight === visibleHalf) {
        html.push(this._buildItem('page', this._config.totalPages))
      } else if (marginRight > visibleHalf) {
        html.push(this._buildItem('seperator'), this._buildItem('page', this._config.totalPages))
      }
    }

    return html.join('')
  }

  _buildItem(type, pageData) {
    // console.debug(type)
    // console.debug(pageData)
    const html = this._config[type]
      .replace(/{{page}}/g, pageData)
      .replace(/{{totalPages}}/g, this._config.totalPages)
      .replace(/{{totalCounts}}/g, this._config.totalCounts)
      .replace(/{{selectOptions}}/g, this._buildSelectOptions())

    return $(html)
      .attr({
        'paginator-role': type,
        'paginator-data': pageData
      })
      .paginatorHTML()
  }

  _buildSelectOptions() {
    const html = []
    for (let i = 1, j = this._config.totalPages; i <= j; i++) {
      html.push(`<option value="${i}">${i}</option>`)
    }

    return html.join('')
  }

  _setStatus() {
    if (!this._isEnable('first') || this._config.currentPage === 1) {
      $(SELECTOR_DATA_PAGINATOR_FIRST, this._element).addClass(this._config.disableClass)
    }

    if (!this._isEnable('previousJump') || this._config.currentPage === 1) {
      $(SELECTOR_DATA_PAGINATOR_PREVIOUS_JUMP, this._element).addClass(this._config.disableClass)
    }

    if (!this._isEnable('previous') || this._config.currentPage === 1) {
      $(SELECTOR_DATA_PAGINATOR_PREVIOUS, this._element).addClass(this._config.disableClass)
    }

    if (!this._isEnable('next') || this._config.currentPage >= this._config.totalPages) {
      $(SELECTOR_DATA_PAGINATOR_NEXT, this._element).addClass(this._config.disableClass)
    }

    if (!this._isEnable('nextJump') || this._config.currentPage === this._config.totalPages) {
      $(SELECTOR_DATA_PAGINATOR_NEXT_JUMP, this._element).addClass(this._config.disableClass)
    }

    if (!this._isEnable('last') || this._config.currentPage >= this._config.totalPages) {
      $(SELECTOR_DATA_PAGINATOR_LAST, this._element).addClass(this._config.disableClass)
    }

    $(SELECTOR_DATA_PAGINATOR_PAGE, this._element).removeClass(this._config.activeClass)
    $(`${SELECTOR_DATA_PAGINATOR_PAGE}[paginator-data=${this._config.currentPage}]`, this._element).addClass(this._config.activeClass)

    $(`${SELECTOR_DATA_PAGINATOR_PAGE_SELECT}[paginator-data=${this._config.currentPage}]`, this._element).find('select').val(this._config.currentPage)
  }

  _getFixedPages() {
    const pages = []

    let _visiblePages = this._config.visiblePages
    const _currentPage = this._config.currentPage
    const _totalPages = this._config.totalPages

    if (_visiblePages > _totalPages) {
      _visiblePages = _totalPages
    }

    // const half = Math.floor(visiblePages / 2)
    // const start = currentPage - half + 1 - (visiblePages % 2)
    // const end = currentPage + half
    let start = (Math.floor((_currentPage - 1) / _visiblePages) * _visiblePages) + 1
    let end = start + _visiblePages - 1 > _totalPages ? _totalPages : start + _visiblePages - 1
    // console.debug(currentPage)
    // console.debug(visiblePages)
    // console.debug(totalPages)
    // console.debug(start)
    // console.debug(end)

    if (start < 1) {
      start = 1
      end = _visiblePages
    }

    if (end > _totalPages) {
      end = _totalPages
      start = 1 + _totalPages - _visiblePages
    }

    let itPage = start
    while (itPage <= end) {
      pages.push(itPage)
      itPage++
    }

    return pages
  }

  // _getPages() {
  //   const pages = []

  //   const visiblePages = this._config.visiblePages

  //   const currentPage = this._config.currentPage

  //   const totalPages = this._config.totalPages

  //   if (visiblePages > totalPages) {
  //     visiblePages = totalPages
  //   }

  //   const half = Math.floor(visiblePages / 2)
  //   const start = currentPage - half + 1 - (visiblePages % 2)
  //   const end = currentPage + half

  //   if (start < 1) {
  //     start = 1
  //     end = visiblePages
  //   }

  //   if (end > totalPages) {
  //     end = totalPages
  //     start = 1 + totalPages - visiblePages
  //   }

  //   const itPage = start
  //   while (itPage <= end) {
  //     pages.push(itPage)
  //     itPage++
  //   }

  //   return pages
  // }

  _bindEvents() {
    this._element.off()

    this._element.on('click',
      SELECTOR_DATA_PAGINATOR_PAGE_BUTTONS,
      event => {
        // console.debug(e.data)
        // var $el = $(this)
        // if ($el.hasClass(e.data.disableClass) ||
        //   $el.hasClass(e.data.activeClass) ||
        //   $el.attr('paginator-role') === 'statistics' ||
        //   $el.attr('paginator-role') === 'seperator' ||
        //   $el.attr('paginator-role') === 'pageSelect' ||
        //   $el.attr('paginator-role') === 'page-input' ||
        //   $el.attr('paginator-role') === 'page-go-button') {
        //   return
        // }

        // console.debug(typeof e.data.this._config.onPageChange)
        const pageIndex = Number($(event.currentTarget).attr('paginator-data'))
        this.switchPage(pageIndex)
      })

    this._element.on('change', `${SELECTOR_DATA_PAGINATOR_PAGE_SELECT} > select`, event => {
      const pageIndex = Number($(event.currentTarget).val())
      this.switchPage(pageIndex)
    })

    this._element.on('submit', SELECTOR_DATA_PAGINATOR_PAGINATION_INFO_FORM, this._element, event => {
      this._goPage(event.target)
      return false
    })

    // this._element.on('click', SELECTOR_DATA_PAGINATOR_PAGE_GO_BUTTON, this._element, event => {
    //   this._goPage(event.target)
    // })
  }

  _goPage(paginatorElement) {
    const $paginatorInput = $(paginatorElement).find(SELECTOR_DATA_PAGINATOR_PAGE_INPUT)

    try {
      const pageIndex = Number($paginatorInput.val())
      if (!isNaN(pageIndex)) {
        this.switchPage(pageIndex)
      }
    } catch {
      throw new TypeError('[paginator] Invalid Input Value!')
    }

    $paginatorInput.val('')
  }

  _isNumber(value) {
    const type = typeof value
    return type === 'number' || type === 'undefined'
  }

  _isEnable(type) {
    return this._config[type] && typeof this._config[type] === 'string'
  }

  _init() {
    if (this._config.first || this._config.previousJump || this._config.previous ||
      this._config.next || this._config.nextJump || this._config.last ||
      this._config.page ||
      this._config.pageSelect) {
      this._config = $.extend(
        {},
        {
          first: '',
          previousJump: '',
          previous: '',
          next: '',
          nextJump: '',
          last: '',
          page: '',
          pageSelect: ''
        },
        this._config
      )
    }

    this._config = $.extend({}, Default, this._config)

    this._verify()

    this._setAttributes()

    this._extendJquery()

    this._render()
  }

  // Static

  static _jQueryInterface(config) {
    return this.each(function () {
      let data = $(this).data(DATA_KEY)

      if (!data) {
        data = $(this).data()
      }

      const __config = $.extend({}, Default, typeof config === 'object' ? config : data)
      const plugin = new Paginator($(this), __config)

      $(this).data(DATA_KEY, typeof config === 'object' ? config : data)

      if (config === 'init') {
        plugin[config]()
      }
    })
  }
}

/**
 * Data API
 * ====================================================
 */

// $(window).on('load', () => {
//   Paginator._jQueryInterface.call($(SELECTOR_DATA_TOGGLE))
// })

/**
 * jQuery API
 * ====================================================
 */

$.fn[NAME] = Paginator._jQueryInterface
$.fn[NAME].Constructor = Paginator
$.fn[NAME].noConflict = function () {
  $.fn[NAME] = JQUERY_NO_CONFLICT
  return Paginator._jQueryInterface
}

export default Paginator
